import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { apiClient } from '@/api/client';
import { Search, Edit, Plus, RefreshCw, Download, DollarSign, AlertCircle, X, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose
} from '@/components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';

import { SmsPricingSection } from './billing/SmsPricingSection';
import { RestaurantSmsStatusList } from './billing/RestaurantSmsStatusList';
import { WebhookEventsList } from './billing/WebhookEventsList';
import { BillingUsageCharts } from './billing/BillingUsageCharts';
import { PaymentHistoryList } from './billing/PaymentHistoryList';
import { EnhancedSmsEnableDialog } from './billing/EnhancedSmsEnableDialog';

// Add more imports as needed for your Stripe integration

interface SuperAdminBillingTabProps {
  organisations: any[];
  restaurants: any[];
  // Add additional props as needed
}

const SuperAdminBillingTab: React.FC<SuperAdminBillingTabProps> = ({ 
  organisations,
  restaurants
}) => {
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Add state for Stripe billing data
  const [showEnhancedSmsDialog, setShowEnhancedSmsDialog] = useState(false);
  const [stripeCustomers, setStripeCustomers] = useState<any[]>([]);
  const [webhookEvents, setWebhookEvents] = useState<any[]>([]);
  const [billingHistory, setBillingHistory] = useState<any[]>([]);
  const [showAddStripeModal, setShowAddStripeModal] = useState(false);
  const [newStripeCustomerId, setNewStripeCustomerId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingData, setPricingData] = useState({
    price: '0.15',
    currency: 'NZD'
  });
  
  // Filter organisations based on search query
  const filteredOrganisations = organisations.filter(org =>
    org.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    org.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Load Stripe customers when an organisation is selected
  useEffect(() => {
    if (selectedOrganisationId) {
      loadStripeData(selectedOrganisationId);
    }
  }, [selectedOrganisationId]);

  // Load Stripe data for the selected organisation
  const loadStripeData = async (organisationId: string) => {
    setLoading(true);
    try {
      // Fetch Stripe customer mapping
      const { data: stripeMapping, error: mappingError } = await supabase
        .from('organisation_stripe_mapping')
        .select('*')
        .eq('organisation_id', organisationId)
        .single();
        
      if (mappingError) throw mappingError;
      
      // Get current pricing
      const { data: pricing, error: pricingError } = await supabase
        .from('sms_price_configuration')
        .select('*')
        .eq('organisation_id', organisationId)
        .maybeSingle();
        
      if (!pricingError && pricing) {
        setPricingData({
          price: pricing.price_per_segment.toString(),
          currency: pricing.currency
        });
      } else {
        // Set default pricing
        setPricingData({
          price: '0.15',
          currency: 'NZD'
        });
      }
      
      if (stripeMapping?.stripe_customer_id) {
        // Load webhook events related to this customer
        const { data: webhooks, error: webhooksError } = await supabase
          .from('stripe_webhook_events')
          .select('*')
          .order('processed_at', { ascending: false })
          .limit(100);
          
        if (!webhooksError && webhooks) {
          setWebhookEvents(webhooks);
        }
        
        // Here you would fetch additional Stripe data as needed
        // This is a placeholder for actual API calls to fetch detailed billing data
        
        // Set the Stripe customer data
        setStripeCustomers([stripeMapping]);
      }
    } catch (error) {
      console.error('Error loading Stripe data:', error);
      toast({
        title: "Failed to load Stripe data",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to add a Stripe customer to the organisation
  const addStripeCustomer = async () => {
    if (!selectedOrganisationId || !newStripeCustomerId) return;
    
    setSubmitting(true);
    try {
        // Use apiClient instead of direct fetch
        const result = await apiClient.post('/api/admin/stripe/update-customer-id', {
          organisation_id: selectedOrganisationId,
          stripe_customer_id: newStripeCustomerId
        });
        
        if (result.data.success) {
          toast({
            title: "Stripe Integration Created",
            description: "The organisation is now connected to Stripe",
          });
          
          // Reload Stripe data
          loadStripeData(selectedOrganisationId);
          
          // Close the modal and reset the form
          setShowAddStripeModal(false);
          setNewStripeCustomerId('');
        } else {
          throw new Error(result.data.error || 'Unknown error occurred');
        }
      } catch (error) {
        console.error('Error adding Stripe customer:', error);
        toast({
          title: "Failed to create Stripe integration",
          description: error instanceof Error ? error.message : "Unknown error",
          variant: "destructive"
        });
      } finally {
        setSubmitting(false);
      }
    };

  // Function to update pricing
  const updatePricing = async () => {
    if (!selectedOrganisationId) return;
    
    setSubmitting(true);
    try {
        // Use apiClient instead of direct fetch
        const result = await apiClient.post('/api/admin/stripe/pricing', {
          organisation_id: selectedOrganisationId,
          price_per_segment: parseFloat(pricingData.price),
          currency: pricingData.currency
        });
        
        if (result.data.success) {
          toast({
            title: "Pricing Updated",
            description: "SMS pricing has been updated successfully",
          });
          
          // Close the modal and reset the form
          setShowPricingModal(false);
        } else {
          throw new Error(result.data.error || 'Unknown error occurred');
        }
      } catch (error) {
        console.error('Error updating pricing:', error);
        toast({
          title: "Failed to update pricing",
          description: error instanceof Error ? error.message : "Unknown error",
          variant: "destructive"
        });
      } finally {
        setSubmitting(false);
      }
    };

  // Function to update a Stripe customer ID
  const handleEditCustomerID = () => {
    // Using the same modal for editing as for adding
    setNewStripeCustomerId(stripeCustomers[0]?.stripe_customer_id || '');
    setShowAddStripeModal(true);
  };

  // Function to export billing data as CSV
  const exportBillingData = async () => {
    if (!selectedOrganisationId || !stripeCustomers[0]?.stripe_customer_id) return;
    
    try {
        // Use apiClient instead of direct fetch
        const result = await apiClient.get(`/api/admin/stripe/payments?customer_id=${stripeCustomers[0].stripe_customer_id}`);
        
        if (result.data.success && result.data.payments) {
          // Create CSV content
          let csvContent = "Invoice ID,Date,Amount,Status\n";
          
          result.data.payments.forEach((payment: any) => {
            csvContent += `"${payment.invoice_id || 'N/A'}","${new Date(payment.payment_date).toLocaleDateString()}","${payment.amount}","${payment.status}"\n`;
          });
          
          // Create and download file
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          const orgName = organisations.find(org => org.id === selectedOrganisationId)?.name || 'organisation';
          
          link.setAttribute('href', url);
          link.setAttribute('download', `${orgName}-billing-${new Date().toISOString().split('T')[0]}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          toast({
            title: "Export Successful",
            description: "Billing data has been downloaded",
          });
        } else {
          throw new Error('No payment data available');
        }
      } catch (error) {
        console.error('Error exporting billing data:', error);
        toast({
          title: "Export Failed",
          description: error instanceof Error ? error.message : "Unknown error",
          variant: "destructive"
        });
      }
    };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Billing Management</h2>
        
        <div className="flex space-x-4">
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground/70" />
            <Input
              placeholder="Search organisations..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <Button 
            variant="outline" 
            onClick={() => selectedOrganisationId && loadStripeData(selectedOrganisationId)}
            disabled={!selectedOrganisationId || loading}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh Data
          </Button>
        </div>
      </div>
      
      {/* Organisation selector */}
      <div className="flex items-center space-x-4">
        <Select onValueChange={setSelectedOrganisationId} value={selectedOrganisationId}>
          <SelectTrigger className="w-[350px]">
            <SelectValue placeholder="Select an organisation" />
          </SelectTrigger>
          <SelectContent>
            {filteredOrganisations.map(org => (
              <SelectItem key={org.id} value={org.id}>
                {org.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {selectedOrganisationId && (
          <Button variant="outline" onClick={() => setSelectedOrganisationId('')}>
            Clear Selection
          </Button>
        )}
      </div>
      
      {/* Main content area */}
      {selectedOrganisationId ? (
        <Tabs defaultValue="overview">
          <TabsList className="grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap h-auto w-full mb-4 p-1">
            <TabsTrigger value="overview" className="sm:flex-[1_0_16%]">Overview</TabsTrigger>
            <TabsTrigger value="pricing" className="sm:flex-[1_0_16%]">SMS Pricing</TabsTrigger>
            <TabsTrigger value="restaurants" className="sm:flex-[1_0_16%]">Restaurant Status</TabsTrigger>
            <TabsTrigger value="payments" className="sm:flex-[1_0_16%]">Payment History</TabsTrigger>
            <TabsTrigger value="webhooks" className="sm:flex-[1_0_16%]">Webhook Events</TabsTrigger>
            <TabsTrigger value="usage" className="sm:flex-[1_0_16%]">Usage Reports</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Stripe Integration Overview</CardTitle>
                <CardDescription>
                  Manage Stripe billing integration for SMS messaging
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="py-4 text-center">Loading Stripe data...</div>
                ) : stripeCustomers.length > 0 ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Stripe Customer Card */}
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium">
                            Stripe Customer
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold truncate">
                            {stripeCustomers[0].stripe_customer_id || 'Not connected'}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Created: {new Date(stripeCustomers[0].created_at).toLocaleDateString()}
                          </p>
                        </CardContent>
                      </Card>
                      
                      {/* SMS Status Card */}
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium">
                            SMS Status
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold">
                            <div className="flex items-center space-x-2">
                              <Switch
                                checked={stripeCustomers[0].sms_enabled}
                                onCheckedChange={async (checked) => {
                                  try {
                                    const result = await apiClient.post(
                                      `/api/admin/stripe/${checked ? 'enable' : 'disable'}-organisation-sms`,
                                      { organisation_id: selectedOrganisationId }
                                    );
                                    
                                    if (result.data.success) {
                                      toast({
                                        title: `SMS ${checked ? 'Enabled' : 'Disabled'}`,
                                        description: `SMS has been ${checked ? 'enabled' : 'disabled'} for this organisation`,
                                      });
                                      loadStripeData(selectedOrganisationId);
                                    } else {
                                      throw new Error(result.data.error || 'Unknown error occurred');
                                    }
                                  } catch (error) {
                                    console.error('Error toggling SMS status:', error);
                                    toast({
                                      title: "Failed to update SMS status",
                                      description: error instanceof Error ? error.message : "Unknown error",
                                      variant: "destructive"
                                    });
                                  }
                                }}
                              />
                              <span className={stripeCustomers[0].sms_enabled ? "text-green-500" : "text-red-500"}>
                                {stripeCustomers[0].sms_enabled ? 'Enabled' : 'Disabled'}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {stripeCustomers[0].sms_enabled 
                                ? `Enabled on: ${new Date(stripeCustomers[0].sms_enabled_date).toLocaleDateString()}`
                                : 'SMS messaging is currently disabled'
                              }
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      
                      {/* Additional cards as needed */}
                    </div>
                    
                    {/* Actions section */}
                    <div className="flex space-x-4">
                      <Button 
                        variant="outline"
                        onClick={handleEditCustomerID}
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Edit Customer ID
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => setShowPricingModal(true)}
                      >
                        <DollarSign className="mr-2 h-4 w-4" />
                        Update Pricing
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={exportBillingData}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Export Billing Data
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground" />
                    <h3 className="mt-4 text-lg font-medium">No Stripe Integration</h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      This organisation doesn't have a Stripe customer mapping yet.
                    </p>
                    <Button 
                      className="mt-4"
                      onClick={() => setShowEnhancedSmsDialog(true)}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Set Up Stripe Integration
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="pricing" className="space-y-4">
            <SmsPricingSection 
              organisationId={selectedOrganisationId} 
              restaurants={restaurants.filter(r => r.organisation_id === selectedOrganisationId)}
            />
          </TabsContent>
          
          <TabsContent value="restaurants" className="space-y-4">
            <RestaurantSmsStatusList 
              organisationId={selectedOrganisationId}
              restaurants={restaurants.filter(r => r.organisation_id === selectedOrganisationId)}
            />
          </TabsContent>
          
          <TabsContent value="payments" className="space-y-4">
            <PaymentHistoryList 
              organisationId={selectedOrganisationId}
              stripeCustomerId={stripeCustomers[0]?.stripe_customer_id}
            />
          </TabsContent>
          
          <TabsContent value="webhooks" className="space-y-4">
            <WebhookEventsList 
              events={webhookEvents}
              loading={loading}
            />
          </TabsContent>
          
          <TabsContent value="usage" className="space-y-4">
            <BillingUsageCharts 
              organisationId={selectedOrganisationId}
            />
          </TabsContent>
        </Tabs>
      ) : (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground">
              Select an organisation to manage its billing settings
            </p>
          </CardContent>
        </Card>
      )}

      {/* Add Stripe Customer Modal */}
      <Dialog open={showAddStripeModal} onOpenChange={setShowAddStripeModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {stripeCustomers.length > 0 ? 'Edit Stripe Customer ID' : 'Set Up Stripe Integration'}
            </DialogTitle>
            <DialogDescription>
              {stripeCustomers.length > 0 
                ? 'Update the Stripe Customer ID for this organisation.'
                : 'Enter a Stripe Customer ID to connect with this organisation.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="stripeCustomerId" className="col-span-4">
                Stripe Customer ID
              </Label>
              <Input
                id="stripeCustomerId"
                placeholder="cus_..."
                className="col-span-4"
                value={newStripeCustomerId}
                onChange={(e) => setNewStripeCustomerId(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddStripeModal(false);
                if (stripeCustomers.length > 0) {
                  setNewStripeCustomerId('');
                }
              }}
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button 
              onClick={addStripeCustomer} 
              disabled={submitting || !newStripeCustomerId}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  {stripeCustomers.length > 0 ? 'Update Customer' : 'Add Customer'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Pricing Modal */}
      <Dialog open={showPricingModal} onOpenChange={setShowPricingModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Update SMS Pricing</DialogTitle>
            <DialogDescription>
              Set the price per SMS segment for this organisation.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="price" className="col-span-4">
                Price per SMS Segment
              </Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.15"
                className="col-span-4"
                value={pricingData.price}
                onChange={(e) => setPricingData({...pricingData, price: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="currency" className="col-span-4">
                Currency
              </Label>
              <Select 
                value={pricingData.currency} 
                onValueChange={(value) => setPricingData({...pricingData, currency: value})}
              >
                <SelectTrigger className="col-span-4">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NZD">NZD - New Zealand Dollar</SelectItem>
                  <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="GBP">GBP - British Pound</SelectItem>
                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPricingModal(false)}
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button 
              onClick={updatePricing} 
              disabled={submitting || !pricingData.price}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <DollarSign className="mr-2 h-4 w-4" />
                  Update Pricing
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enhanced SMS Enable Dialog */}
      {selectedOrganisationId && (
        <EnhancedSmsEnableDialog 
          open={showEnhancedSmsDialog}
          onOpenChange={setShowEnhancedSmsDialog}
          organisationId={selectedOrganisationId}
          organisationName={organisations.find(org => org.id === selectedOrganisationId)?.name || 'Organisation'}
          onSuccess={() => loadStripeData(selectedOrganisationId)}
        />
      )}
    </div>
  );
};

export default SuperAdminBillingTab;